// The two pieces of TON encoding the wallet needs at startup, without @ton/core.
//
// A TON address is the hash of the wallet contract's initial state, so showing
// a user their address means hashing cells — and validating a recipient means
// decoding an address. @ton/core does both, but it does not tree-shake: BOC
// serialization, the dictionary implementation and a deflate are all pulled in
// together. Measured on the Mini App's entry bundle, that was +315 kB raw and
// +91 kB gzipped on every open, to run two functions.
//
// So the two functions are here, over sha256, which this package already has.
// This is encoding on the money path, so nothing about it is inferred: every
// value below was read off @ton/core, and the test beside this file checks the
// results against @ton/core across many keys and every address form. The full
// library still loads when a transfer is SIGNED — that path is lazy, and
// building a signed message is more than two functions' worth of machinery.
//
// References: the cell representation is from the TON whitepaper's "standard
// cell representation"; the address encoding is TEP-2.

import { sha256 } from '@noble/hashes/sha256';

/** A cell, reduced to what hashing needs: its bits and its children's
 *  identities. A child is already-hashed, so the tree is never held at once. */
export interface CellRepr {
  /** Big-endian bits, left-aligned in `bytes`. */
  bitLength: number;
  bytes: Uint8Array;
  refs: { hash: Uint8Array; depth: number }[];
}

/**
 * The standard representation of a cell, which is what gets hashed.
 *
 * Two descriptor bytes, then the data, then every child's depth, then every
 * child's hash. The data is "augmented" when the bit count is not a multiple
 * of eight: a 1 bit marks the end and the rest of the byte is zero. Dropping
 * that marker yields a valid-looking hash for a different cell.
 */
export function cellRepr(cell: CellRepr): Uint8Array {
  const { bitLength, bytes, refs } = cell;
  const fullBytes = Math.floor(bitLength / 8);
  const remainder = bitLength % 8;
  const dataLength = Math.ceil(bitLength / 8);

  // d1 carries the reference count (plus flags for exotic cells, which we
  // never build); d2 encodes the bit length so a reader can recover it.
  const d1 = refs.length;
  const d2 = dataLength + fullBytes;

  const out = new Uint8Array(2 + dataLength + refs.length * 2 + refs.length * 32);
  let at = 0;
  out[at++] = d1;
  out[at++] = d2;
  out.set(bytes.subarray(0, fullBytes), at);
  at += fullBytes;
  if (remainder) {
    const kept = bytes[fullBytes]! & (0xff << (8 - remainder));
    out[at++] = kept | (1 << (7 - remainder));
  }
  // Depths first, then hashes — not interleaved.
  for (const ref of refs) {
    out[at++] = (ref.depth >> 8) & 0xff;
    out[at++] = ref.depth & 0xff;
  }
  for (const ref of refs) {
    out.set(ref.hash, at);
    at += 32;
  }
  return out;
}

export function cellHash(cell: CellRepr): Uint8Array {
  return sha256(cellRepr(cell));
}

/** A cell's depth: one more than its deepest child, zero with no children. */
export function cellDepth(refs: { depth: number }[]): number {
  return refs.length === 0 ? 0 : Math.max(...refs.map((r) => r.depth)) + 1;
}

// ── address encoding (TEP-2) ────────────────────────────────────────────────

const TAG_BOUNCEABLE = 0x11;
const TAG_NON_BOUNCEABLE = 0x51;
const TAG_TESTNET = 0x80;

/**
 * CRC-16/XMODEM — the checksum in a user-friendly address.
 *
 * It is why a mistyped address is rejected rather than sent into nothing, so
 * the polynomial and the zero initial value are both load bearing.
 */
export function crc16Xmodem(data: Uint8Array): number {
  let crc = 0;
  for (const byte of data) {
    crc ^= byte << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc;
}

export interface TonAddressParts {
  workchain: number;
  hash: Uint8Array;
  bounceable: boolean;
  testOnly: boolean;
}

/** Encode a user-friendly address. Base64url, which is what wallets show. */
export function encodeTonAddress(
  workchain: number,
  hash: Uint8Array,
  bounceable: boolean,
  testOnly = false,
): string {
  if (hash.length !== 32) throw new Error('ton: expected a 32-byte hash');
  const body = new Uint8Array(34);
  body[0] = (bounceable ? TAG_BOUNCEABLE : TAG_NON_BOUNCEABLE) | (testOnly ? TAG_TESTNET : 0);
  // Workchain is a signed byte: 0 is the basechain, -1 (0xff) the masterchain.
  body[1] = workchain & 0xff;
  body.set(hash, 2);

  const crc = crc16Xmodem(body);
  const full = new Uint8Array(36);
  full.set(body);
  full[34] = (crc >> 8) & 0xff;
  full[35] = crc & 0xff;

  // Standard base64, then the url-safe alphabet. Node's Buffer knows the name
  // 'base64url'; the Mini App's Buffer polyfill does not, and throws
  // "Unknown encoding: base64url" — which aborted derivation of every chain,
  // because one wallet derives them all in a single call.
  return Buffer.from(full)
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Decode any form of a TON address — raw (`0:<64 hex>`) or user-friendly, in
 * either base64 alphabet. Returns null rather than throwing: it guards both
 * recipient validation and deposit matching, where one malformed value must
 * not take the caller down.
 */
export function decodeTonAddress(input: string): TonAddressParts | null {
  const text = input.trim();
  if (!text) return null;

  const raw = /^(-?\d+):([0-9a-fA-F]{64})$/.exec(text);
  if (raw) {
    return {
      workchain: Number(raw[1]),
      hash: Buffer.from(raw[2]!, 'hex'),
      // A raw address carries no flag. Bounceable is the conventional reading,
      // and nothing here depends on it: it is the hash that identifies the
      // account.
      bounceable: true,
      testOnly: false,
    };
  }

  // Both alphabets appear in the wild — the url-safe one in wallets, the
  // standard one in older tooling and QR codes.
  if (!/^[A-Za-z0-9+/_-]{48}=?=?$/.test(text)) return null;
  let bytes: Buffer;
  try {
    bytes = Buffer.from(text.replace(/-/g, '+').replace(/_/g, '/'), 'base64');
  } catch {
    return null;
  }
  if (bytes.length !== 36) return null;

  const body = bytes.subarray(0, 34);
  const expected = (bytes[34]! << 8) | bytes[35]!;
  if (crc16Xmodem(body) !== expected) return null;

  let tag = body[0]!;
  const testOnly = (tag & TAG_TESTNET) !== 0;
  tag &= ~TAG_TESTNET;
  if (tag !== TAG_BOUNCEABLE && tag !== TAG_NON_BOUNCEABLE) return null;

  return {
    // Signed: 0xff is the masterchain, -1, not 255.
    workchain: body[1]! === 0xff ? -1 : body[1]!,
    hash: Buffer.from(body.subarray(2)),
    bounceable: tag === TAG_BOUNCEABLE,
    testOnly,
  };
}
